import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { ChatCircleText } from "@phosphor-icons/react";
import {
  createInboxMessageThunk,
  selectInboxMutationsForDocument,
} from "../../slice/InboxSlice";
import { normalizeShareRecipients } from "../../utils/shareRecipients";
import { getShareRecipients } from "../../slice/MatterDetailsSlice";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";

/**
 * Send-message modal (FE Phase 5b).
 *
 * Wraps `POST /api/inbox/items/<docId>/messages/`. Person-to-person note
 * attached to a doc — separate from chat, separate from tasks.
 *
 * Same shape and recipient-loading strategy as `AssignTaskModal`. Kept
 * as its own component (rather than parameterizing one shared modal)
 * because the affordances diverge in v2 (messages will probably grow
 * a thread, tasks won't), and the labels read very differently to
 * users — "Assign" vs "Send" is a different mental model for the
 * primary CTA, and merging them would force awkward generic copy.
 */

const SendMessageModal = ({ open, documentId, onClose }) => {
  const dispatch = useDispatch();
  const [recipient, setRecipient] = useState(null);
  const [ccIds, setCcIds] = useState([]);
  const [body, setBody] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [recipientsStatus, setRecipientsStatus] = useState("idle");
  const [submitError, setSubmitError] = useState(null);

  const mutations = useSelector((state) =>
    selectInboxMutationsForDocument(state, documentId)
  );
  const submitting = mutations.message === "loading";

  useEffect(() => {
    if (!open) return;
    setRecipient(null);
    setCcIds([]);
    setBody("");
    setSubmitError(null);
  }, [open, documentId]);

  useEffect(() => {
    if (!open || documentId == null) return;
    setRecipientsStatus("loading");
    dispatch(getShareRecipients({ docId: documentId, reviewersOnly: false }))
      .unwrap()
      .then((response) => {
        setRecipients(normalizeShareRecipients(response));
        setRecipientsStatus("ready");
      })
      .catch((err) => {
        setRecipientsStatus("failed");
        setSubmitError(
          err?.message ||
            "Couldn't load the list of teammates to send the message to."
        );
      });
  }, [open, documentId, dispatch]);

  const recipientOptions = useMemo(
    () =>
      recipients
        .filter((r) => r?.id != null)
        .map((r) => ({
          value: r.id,
          label: r.name || r.full_name || r.username || r.email || `User #${r.id}`,
        })),
    [recipients]
  );

  // Cc'd people become full members of the conversation — they see every
  // future reply and can reply themselves, same as the primary recipient.
  const ccOptions = useMemo(
    () => recipientOptions.filter((option) => option.value !== recipient),
    [recipientOptions, recipient]
  );

  useEffect(() => {
    setCcIds((current) => current.filter((id) => id !== recipient));
  }, [recipient]);

  const trimmedBody = body.trim();
  const canSubmit =
    recipient != null && trimmedBody.length > 0 && !submitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit || !documentId) return;
    setSubmitError(null);
    dispatch(
      createInboxMessageThunk({
        documentId,
        recipientUserId: recipient,
        ccUserIds: ccIds,
        body: trimmedBody,
      })
    )
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((err) => {
        setSubmitError(
          err?.message || "Couldn't send message. Please try again."
        );
      });
  }, [canSubmit, ccIds, documentId, dispatch, recipient, trimmedBody, onClose]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Send"
      okButtonProps={{ disabled: !canSubmit, loading: submitting }}
      cancelText="Cancel"
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <ChatCircleText size={16} weight="bold" />
          Send message
        </span>
      }
      destroyOnClose
    >
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-message-recipient">To</InboxModalLabel>
        <Select
          id="inbox-message-recipient"
          style={{ width: "100%" }}
          placeholder={
            recipientsStatus === "loading" ? "Loading…" : "Choose a teammate…"
          }
          options={recipientOptions}
          value={recipient}
          onChange={setRecipient}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent={
            recipientsStatus === "loading" ? (
              <Spin size="small" />
            ) : (
              "No matching teammates"
            )
          }
          disabled={recipientsStatus === "loading"}
        />
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-message-cc">Cc</InboxModalLabel>
        <Select
          id="inbox-message-cc"
          mode="multiple"
          style={{ width: "100%" }}
          placeholder="Loop in other teammates (optional)"
          options={ccOptions}
          value={ccIds}
          onChange={setCcIds}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          disabled={recipientsStatus === "loading"}
        />
        <InboxModalHint>
          Cc&apos;d teammates become full members of this conversation — they
          see every reply and can reply themselves.
        </InboxModalHint>
      </InboxModalFieldGroup>

      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="inbox-message-body">Message</InboxModalLabel>
        <Input.TextArea
          id="inbox-message-body"
          autoSize={{ minRows: 3, maxRows: 8 }}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="e.g. Heads up — opposing counsel filed yesterday; please prioritize."
          maxLength={2000}
          showCount
        />
        <InboxModalHint>
          The recipient will receive a notification linking back to this
          document. Future replies stay together in this conversation.
        </InboxModalHint>
      </InboxModalFieldGroup>

      {submitError && (
        <InboxModalErrorBanner role="alert">{submitError}</InboxModalErrorBanner>
      )}
    </Modal>
  );
};

SendMessageModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
};

export default SendMessageModal;
